import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, TrendingUp, TrendingDown, Target, Activity } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface GroupStats {
  group_id: string;
  group_name: string;
  group_type: string;
  total_signals: number;
  forwarded: number;
  skipped: number;
  skipped_by_group: number;
  wins: number;
  losses: number;
  win_rate: number;
  bot_distribution: Array<{ bot_id: string; bot_name: string; signal_count: number }>;
}

export const GroupAnalytics: React.FC = () => {
  const { data: groupStats, isLoading } = useQuery({
    queryKey: ['group-analytics'],
    queryFn: async () => {
      // @ts-ignore - strategy_groups table
      const { data: groups } = await supabase
        // @ts-ignore
        .from('strategy_groups')
        .select('id, name, group_type, is_active')
        .eq('is_active', true);

      if (!groups || groups.length === 0) return [];

      const statsPromises = groups.map(async (group) => {
        // Get bot IDs in group
        // @ts-ignore - strategy_group_members table
        const { data: members } = await supabase
          // @ts-ignore
          .from('strategy_group_members')
          .select('bot_id, bots(name)')
          .eq('group_id', group.id)
          .eq('is_active', true);

        const botIds = members?.map(m => m.bot_id) || [];

        if (botIds.length === 0) {
          return {
            group_id: group.id,
            group_name: group.name,
            group_type: group.group_type,
            total_signals: 0,
            forwarded: 0,
            skipped: 0,
            skipped_by_group: 0,
            wins: 0,
            losses: 0,
            win_rate: 0,
            bot_distribution: []
          };
        }

        // Get events for this group's bots
        const { data: events } = await supabase
          .from('conditional_skip_events')
          .select('bot_id, decision, reason, win, bots(name)')
          .in('bot_id', botIds);

        const total_signals = events?.length || 0;
        const forwarded = events?.filter(e => e.decision === 'FORWARDED').length || 0;
        const skipped = events?.filter(e => e.decision === 'SKIPPED').length || 0;
        const skipped_by_group = events?.filter(e => 
          e.reason?.includes('Strategy Group') || e.reason?.includes('strategy group')
        ).length || 0;

        // Calculate wins/losses from forwarded exits only
        const exitEvents = events?.filter(e => 
          e.decision === 'FORWARDED' && e.win !== null
        ) || [];
        const wins = exitEvents.filter(e => e.win === 1).length;
        const losses = exitEvents.filter(e => e.win === 0).length;
        const win_rate = exitEvents.length > 0 ? (wins / exitEvents.length) * 100 : 0;

        // Bot distribution
        const botCounts = new Map<string, { name: string; count: number }>();
        events?.forEach(e => {
          const existing = botCounts.get(e.bot_id);
          const botName = (e as any).bots?.name || 'Unknown';
          if (existing) {
            existing.count++;
          } else {
            botCounts.set(e.bot_id, { name: botName, count: 1 });
          }
        });

        const bot_distribution = Array.from(botCounts.entries())
          .map(([bot_id, data]) => ({
            bot_id,
            bot_name: data.name,
            signal_count: data.count
          }))
          .sort((a, b) => b.signal_count - a.signal_count);

        return {
          group_id: group.id,
          group_name: group.name,
          group_type: group.group_type,
          total_signals,
          forwarded,
          skipped,
          skipped_by_group,
          wins,
          losses,
          win_rate,
          bot_distribution
        };
      });

      return await Promise.all(statsPromises);
    },
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!groupStats || groupStats.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Group Analytics</CardTitle>
          <CardDescription>Performance metrics for strategy groups</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No active strategy groups with signals yet
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {groupStats.map((stats) => (
        <Card key={stats.group_id}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {stats.group_name}
                  <Badge variant="outline">{stats.group_type}</Badge>
                </CardTitle>
                <CardDescription>Performance Overview</CardDescription>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-2xl font-bold">
                    {stats.win_rate > 0 ? stats.win_rate.toFixed(1) : '0.0'}%
                  </div>
                  <div className="text-xs text-muted-foreground">Win Rate</div>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {/* Total Signals */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Activity className="h-4 w-4" />
                  Total Signals
                </div>
                <div className="text-2xl font-bold">{stats.total_signals}</div>
              </div>

              {/* Forwarded */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <TrendingUp className="h-4 w-4" />
                  Forwarded
                </div>
                <div className="text-2xl font-bold text-primary">{stats.forwarded}</div>
                <div className="text-xs text-muted-foreground">
                  {stats.total_signals > 0 
                    ? ((stats.forwarded / stats.total_signals) * 100).toFixed(0) 
                    : '0'}%
                </div>
              </div>

              {/* Skipped by Group */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <TrendingDown className="h-4 w-4" />
                  Skipped by Group
                </div>
                <div className="text-2xl font-bold text-secondary">{stats.skipped_by_group}</div>
                <div className="text-xs text-muted-foreground">
                  {stats.total_signals > 0 
                    ? ((stats.skipped_by_group / stats.total_signals) * 100).toFixed(0) 
                    : '0'}%
                </div>
              </div>

              {/* Win/Loss */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Target className="h-4 w-4" />
                  Win/Loss
                </div>
                <div className="text-2xl font-bold">
                  <span className="text-green-600">{stats.wins}</span>
                  <span className="text-muted-foreground mx-1">/</span>
                  <span className="text-red-600">{stats.losses}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {stats.wins + stats.losses} trades
                </div>
              </div>
            </div>

            {/* Bot Distribution */}
            {stats.bot_distribution.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium">Bot Signal Distribution</h4>
                {stats.bot_distribution.map((bot) => {
                  const percentage = stats.total_signals > 0 
                    ? (bot.signal_count / stats.total_signals) * 100 
                    : 0;
                  return (
                    <div key={bot.bot_id} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{bot.bot_name}</span>
                        <span className="text-muted-foreground">
                          {bot.signal_count} ({percentage.toFixed(0)}%)
                        </span>
                      </div>
                      <Progress value={percentage} className="h-2" />
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
